import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { useDismissable } from '../lib/hooks';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keeps Tab inside the overlay and restores focus to the trigger when it closes. */
function useFocusTrap(open: boolean, containerRef: React.RefObject<HTMLElement>) {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    if (container) {
      const target =
        container.querySelector<HTMLElement>('[data-autofocus]') ??
        container.querySelector<HTMLElement>(FOCUSABLE) ??
        container;
      window.setTimeout(() => target.focus(), 0);
    }
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open, containerRef]);

  return useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Tab') return;
      const container = containerRef.current;
      if (!container) return;
      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement,
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [containerRef],
  );
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'wide',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'narrow' | 'wide' | 'xwide' | 'full';
}): JSX.Element | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const onKeyDown = useFocusTrap(open, containerRef);
  useDismissable(open, onClose);

  if (!open) return null;

  return (
    <div className="overlay-root">
      <div className="overlay-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className={`drawer drawer-${width}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={containerRef}
        onKeyDown={onKeyDown}
      >
        <header className="drawer-head">
          <div>
            <h2 className="drawer-title">{title}</h2>
            {description ? <p className="drawer-description">{description}</p> : null}
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer ? <footer className="drawer-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}): JSX.Element | null {
  const containerRef = useRef<HTMLDivElement>(null);
  const onKeyDown = useFocusTrap(open, containerRef);
  useDismissable(open, onClose);

  if (!open) return null;

  return (
    <div className="overlay-root">
      <div className="overlay-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={containerRef}
        onKeyDown={onKeyDown}
      >
        <header className="modal-head">
          <h2 className="modal-title">{title}</h2>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

/**
 * Confirmation prompt that optionally collects a reason. Used for reject, disconnect and delete.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  tone = 'primary',
  requireReason = false,
  reasonLabel = 'Reason',
  pending = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  requireReason?: boolean;
  reasonLabel?: string;
  pending?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}): JSX.Element | null {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  if (!open) return null;

  const blocked = requireReason && reason.trim().length === 0;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${tone === 'danger' ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => onConfirm(reason.trim())}
            disabled={blocked || pending}
          >
            {pending ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <p className="confirm-message">{message}</p>
      {requireReason ? (
        <label className="field">
          <span className="field-label">{reasonLabel}</span>
          <textarea
            className="input textarea"
            rows={3}
            value={reason}
            data-autofocus
            onChange={(event) => setReason(event.target.value)}
            placeholder="Explain the decision — this is recorded on the audit trail."
          />
        </label>
      ) : null}
    </Modal>
  );
}
